import React, { useState } from 'react';
import { Card, Field, SaveButton } from '../AdminUI';
import { Input } from '../../ui/input';
import { Dialog, DialogContent } from '../../ui/dialog';
import { changeAdminPassword } from '../../../lib/api';
import { useSite } from '../../../context/SiteContext';
import { Lock, RotateCcw, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsSection() {
  const { reset } = useSite();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPwd, setResetPwd] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error('New password must be at least 6 characters');
    if (next !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await changeAdminPassword(cur, next);
      toast.success('Password updated successfully');
      setCur(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    if (!resetPwd) return toast.error('Enter your password to confirm');
    setResetLoading(true);
    try {
      await reset(resetPwd);
      toast.success('All content reset to defaults');
      setResetOpen(false); setResetPwd('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed - check password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Card title="Change Admin Password" desc="Update your admin account password.">
        <form onSubmit={submit} className="space-y-3 max-w-md">
          <Field label="Current Password">
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100" />
          </Field>
          <Field label="New Password" hint="Min 6 characters">
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100" />
          </Field>
          <Field label="Confirm New Password">
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100" />
          </Field>
          <button type="submit" disabled={loading} className="h-10 px-4 rounded-md bg-[color:var(--gold)] hover:brightness-110 disabled:opacity-60 text-black font-bold text-sm flex items-center gap-2">
            <Lock size={14} /> {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </Card>

      <Card title="Danger Zone" desc="Reset all site content to factory defaults. This cannot be undone.">
        <div className="flex items-start gap-3 p-4 rounded-lg border border-red-900/40 bg-red-950/20">
          <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-red-300 font-bold text-sm">Reset all content</h4>
            <p className="text-zinc-400 text-xs mt-1">All your customizations (branding, navigation, products, hero slides, etc.) will be replaced with defaults. Existing user accounts and enquiries are preserved. You'll be asked for your password.</p>
            <button onClick={() => setResetOpen(true)} className="mt-3 h-9 px-3 rounded-md bg-red-600/20 hover:bg-red-600/30 border border-red-900/50 text-red-300 text-xs font-bold flex items-center gap-1.5">
              <RotateCcw size={13} /> Reset all content
            </button>
          </div>
        </div>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md bg-[#1a1a1a] border border-zinc-800 text-zinc-100 p-0 [&>button]:hidden">
          <button onClick={() => setResetOpen(false)} className="absolute right-3 top-3 z-20 w-7 h-7 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 flex items-center justify-center">
            <X size={18} />
          </button>
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-400" /> Confirm Reset
            </h2>
            <p className="text-zinc-400 text-sm mt-2">Enter your admin password to confirm reset. This action cannot be undone.</p>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <Input type="password" autoFocus value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Admin password" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100" />
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setResetOpen(false)} className="h-10 px-4 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-sm">Cancel</button>
              <button onClick={doReset} disabled={resetLoading} className="h-10 px-4 rounded-md bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-bold flex items-center gap-1.5">
                <RotateCcw size={14} /> {resetLoading ? 'Resetting...' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
